/**
 * Cross-tab coordination for the federated session's own renewal cycle
 * (`federatedAccessProvider.ts`). Every tab of this application programs its
 * own `onTokenExpired`, timed independently by the identity client — if two
 * tabs renew at nearly the same instant they redeem the same refresh token
 * twice, and rotation can reject the second redemption exactly like a
 * genuinely expired session (see the wave report, trap 3). This channel
 * never carries the token itself, only a neutral "someone just renewed"
 * signal, so a tab that just heard it can skip its own attempt.
 */
const CHANNEL_NAME = 'pay-admin-session-renewal';
const STORAGE_KEY = 'pay-admin-session-renewed-at';
const COORDINATION_WINDOW_MS = 5_000;

export interface RenewalCoordinator {
  /** True when a sibling tab announced a successful renewal within the coordination window. */
  wasRenewedRecently(): boolean;
  /** Tells sibling tabs this tab just renewed, so they skip their own attempt. */
  announceRenewed(): void;
}

export function createRenewalCoordinator(): RenewalCoordinator {
  let lastRenewedAt = 0;
  const markRenewed = (): void => {
    lastRenewedAt = Date.now();
  };

  let channel: BroadcastChannel | null = null;
  if (typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = markRenewed;
  } else {
    // No BroadcastChannel in this runtime: fall back to the `storage` event,
    // which browsers fire in every other same-origin tab when this key
    // changes. The value carries no session detail, only a timestamp.
    window.addEventListener('storage', (event) => {
      if (event.key === STORAGE_KEY && event.newValue) markRenewed();
    });
  }

  return {
    wasRenewedRecently() {
      return Date.now() - lastRenewedAt < COORDINATION_WINDOW_MS;
    },
    announceRenewed() {
      markRenewed();
      if (channel) {
        channel.postMessage('renewed');
      } else {
        window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
      }
    },
  };
}
